import React, { ReactNode, FormEvent } from 'react'
import assert from 'assert'
import flattenChildren from 'react-keyed-flatten-children'

import { Box } from '../Box/Box'
import { RadioItem } from './RadioItem'
import { Stack } from '../Stack/Stack'
import { SpaceProp } from '../Box/Box.types'

interface RadioGroupContextValues {
  name: string
  value: string
  onChange: (event: FormEvent<HTMLInputElement>) => void
  itemPaddingX: SpaceProp
}

export const RadioGroupContext = React.createContext<RadioGroupContextValues | null>(null)
export const RadioItemContext = React.createContext<number | null>(null)

export interface RadioGroupProps {
  children: ReactNode
  name: string
  value: string
  onChange: (event: FormEvent<HTMLInputElement>) => void
  paddingY?: SpaceProp
  itemSpace?: SpaceProp
  itemPaddingX?: SpaceProp
  dividers?: boolean
  outsideDividers?: boolean
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  onChange,
  paddingY = 'm',
  itemSpace = 'm',
  itemPaddingX = 'm',
  dividers = false,
  outsideDividers = false,
  children
}) => {
  const items = flattenChildren(children)

  const isValidChild = (child: ReactNode) => {
    if (!React.isValidElement(child)) return false

    if (child.type === RadioItem || child.type === RadioGroup) return true

    // If the child has its own children, check them recursively
    if (child.props.children) {
      return React.Children.toArray(child.props.children).some(isValidChild)
    }

    return false
  }

  assert(
    items.every(isValidChild),
    'All direct children of a RadioGroup must be RadioItems or other RadioGroups.'
  )

  return (
    <Box paddingY={paddingY}>
      <RadioGroupContext.Provider value={{ name, value, onChange, itemPaddingX }}>
        <Stack space={itemSpace} dividers={dividers} outsideDividers={outsideDividers}>
          {items.map((item, i) => (
            <RadioItemContext.Provider key={i} value={i}>
              {item}
            </RadioItemContext.Provider>
          ))}
        </Stack>
      </RadioGroupContext.Provider>
    </Box>
  )
}
